/**
 * SigilScreen — Sprint 1.5b.2 (atomic claim + backup)
 *
 * Flow:
 *   1. Utente inserisce sigil
 *   2. Real-time availability check (registerSigilCheck o lookup)
 *   3. Click CLAIM:
 *      a. Cifra mnemonica con (PIN + sigilLower) -> blob
 *      b. POST /registerSigil + /setBackup in sequenza
 *      c. Se uno fallisce, NON salvare niente locale
 *      d. Successo: salva blob localStorage, hydrate store, → /inbox
 *
 * Il PIN viene letto da store.pendingPin (settato da PinSetupScreen).
 * Se manca pendingPin, riportiamo a /pin-setup.
 */

import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Frame, Header, Footer, Btn, Box, FieldLabel, Cursor,
} from '../components/Frame';
import OnboardSteps from '../components/OnboardSteps';
import { useStore } from '../lib/store';
import { callFn, auth } from '../lib/firebase';
import {
  encryptMnemonic,
  saveLastSigil,
  addAccount,
  resetLocalAttempts,
} from '../lib/pinCrypto';
import { mnemonicToSeed } from '../lib/mnemonic';
import { initSodium, keypairFromSeed, boxKeypairFromSeed, toBase64Url } from '../lib/crypto';
import { publishMyKeysFromKeypairs, redeemInvite } from '../lib/messaging';
import { ensureMyPrekeysPublished } from '../lib/fsMessaging';
import { signInAsIdentity } from '../lib/authIdentity';
import { TOS_VERSION, recordTosAcceptanceLocal } from '../lib/legal';

const SIGIL_RE = /^[a-z0-9_]{3,20}$/;

export default function SigilScreen() {
  const navigate = useNavigate();
  const mnemonic = useStore((s) => s.mnemonic);
  const uid = useStore((s) => s.uid);
  const pendingPin = useStore((s) => s.pendingPin);
  const setSigil = useStore((s) => s.setSigil);
  const setPendingPin = useStore((s) => s.setPendingPin);
  const setBoxKeys = useStore((s) => s.setBoxKeys);
  const setFirebaseUser = useStore((s) => s.setFirebaseUser);

  const [input, setInput] = useState('');
  const [status, setStatus] = useState('idle'); // idle | checking | available | taken | invalid
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Una volta che il claim e' andato a buon fine, azzeriamo pendingPin in
  // memoria; senza questo flag la useEffect di guardia sotto interpreterebbe
  // pendingPin=null come "arrivato qui senza setup" e rimanderebbe a
  // /pin-setup, vincendo la race contro navigate('/inbox'). (Bug 2-utenti.)
  const claimedRef = useRef(false);

  // Real-time check
  useEffect(() => {
    setErr(null);
    const clean = input.trim().toLowerCase();
    if (!clean) { setStatus('idle'); return; }
    if (!SIGIL_RE.test(clean)) { setStatus('invalid'); return; }

    setStatus('checking');
    const timer = setTimeout(async () => {
      try {
        const fn = callFn('findUserBySigil');
        const res = await fn({ sigil: clean });
        const p = res.data;
        if (p?.found) setStatus('taken');
        else setStatus('available');
      } catch (e) {
        setStatus('available'); // assume available on transient errors
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [input]);

  async function onClaim() {
    setErr(null);
    const clean = input.trim().toLowerCase();
    if (status !== 'available') return;
    if (!acceptedTerms) {
      setErr('Accetta i Termini e la Privacy per continuare.');
      return;
    }
    if (!mnemonic) {
      setErr('mnemonica persa. ricomincia da capo.');
      return;
    }
    if (!pendingPin) {
      setErr('PIN non impostato. torno indietro al pin setup.');
      setTimeout(() => navigate('/pin-setup', { replace: true }), 1200);
      return;
    }

    setBusy(true);
    try {
      // 0. Deriva le chiavi dalla mnemonica e AUTENTICATI come QUESTA identità.
      //    Auth a token custom (Opzione 2): l'uid Firebase = deriveUid(pubKeySign),
      //    deterministico e mai condiviso con altre identità → scollegabili.
      //    Va fatto PRIMA delle callable (registerSigil/setBackup richiedono auth).
      setStage('chiavi');
      await initSodium();
      const seed = mnemonicToSeed(mnemonic);
      const signKp = await keypairFromSeed(seed);
      const boxKp = await boxKeypairFromSeed(seed);
      const pubKeySignB64 = await toBase64Url(signKp.publicKey);

      setStage('autenticazione');
      await signInAsIdentity({ signPub: signKp.publicKey, signSec: signKp.secretKey });

      // 0b. Riscatta l'invito DALL'IDENTITÀ (Opzione 2): è ciò che sblocca il
      //     gate `invited` di registerSigil e, per gli inviti di connessione,
      //     aggiunge QUESTO uid alla whitelist del creatore + stasha il creatore.
      const pendToken = (() => { try { return sessionStorage.getItem('nullum.pendingInvite'); } catch { return null; } })();
      if (pendToken) {
        setStage('invito');
        const pendPass = (() => { try { return sessionStorage.getItem('nullum.pendingInvitePass'); } catch { return null; } })();
        const ri = await redeemInvite(pendToken, pendPass || null);
        if (!ri?.ok) throw new Error(ri?.error || 'INVITE_REDEEM_FAILED');
        try {
          sessionStorage.removeItem('nullum.pendingInvite');
          sessionStorage.removeItem('nullum.pendingInvitePass');
          if (!ri.registrationOnly && ri.creator?.sigil) {
            sessionStorage.setItem('nullum.connectTo', ri.creator.sigil);
            sessionStorage.setItem('nullum.connectToFull', JSON.stringify(ri.creator));
          }
        } catch {}
      }

      // 1. Cifra mnemonica con PIN + sigil
      setStage('cifratura');
      const bundle = await encryptMnemonic({
        pin: pendingPin,
        sigilLower: clean,
        mnemonic,
      });

      // 2. Claim sigil sul server (passa pubKeySign: il server verifica
      //    deriveUid(pubKeySign)===uid e lega l'identità all'uid).
      setStage('registrazione sigil');
      const regFn = callFn('registerSigil');
      const regRes = await regFn({ sigil: clean, tosVersion: TOS_VERSION, pubKeySign: pubKeySignB64 });
      const regPayload = regRes.data;
      if (regPayload?.ok === false || regPayload?.success === false) {
        throw new Error(regPayload?.error || 'claim sigil rifiutato');
      }

      // 3. Upload backup cifrato (l'utente ora ha il sigil)
      setStage('upload backup');
      const bkFn = callFn('setBackup');
      const bkRes = await bkFn({
        sigilLower: clean,
        blob: bundle.blob,
        salt: bundle.salt,
        nonce: bundle.nonce,
        argon2: bundle.argon2,
      });
      const bkPayload = bkRes.data;
      if (!bkPayload?.ok) {
        // Sigil claimato ma backup fallito: stato inconsistente
        // ma l'utente ha la mnemonica visualizzata, puo' fare /recover
        // dopo. Salviamo comunque la sessione corrente.
        console.warn('backup failed:', bkPayload);
      }

      // 4. Pubblica entrambe le chiavi pubbliche (sign + box): senza pubKeyBox
      //    sul server l'utente è irraggiungibile. Le chiavi sono già derivate (0).
      setStage('pubblicazione chiavi');
      setBoxKeys?.({ boxPub: boxKp.publicKey, boxSec: boxKp.secretKey });
      try {
        await publishMyKeysFromKeypairs(signKp.publicKey, boxKp.publicKey);
      } catch (e) {
        // non-fatale per il claim, ma loggato: senza pubblicazione l'utente
        // resta irraggiungibile finche' non ripassa da una porta di login.
        console.warn('publishKeys failed at claim:', e);
      }
      // Forward Secrecy: pubblica le prekey (firma la SPK con l'identità sign) +
      // persiste i privati su questo device. Fire-and-forget, non blocca il claim.
      ensureMyPrekeysPublished({
        signKeypair: { pub: signKp.publicKey, priv: signKp.secretKey },
        sigil: clean,
      }).catch((e) => console.warn('prekeys publish at claim:', e));

      // 5. Tutto OK: salva SOLO il sigil in localStorage (il blob vive sul
      //    server, rate-limited). Niente blob in locale -> niente brute force
      //    offline da DevTools.
      setStage('finalizzazione');
      saveLastSigil(clean);
      addAccount(clean, { tor: false }); // multi-identità: entra nel catalogo del device
      recordTosAcceptanceLocal(); // accettazione Termini+Privacy (versione+data)
      resetLocalAttempts();
      claimedRef.current = true; // blocca la useEffect di guardia (vedi sotto)
      // Senza firebaseUser nello store, InboxScreen non fa polling delle chat
      // (myFirebaseUid resta null) -> l'utente appena claimato non vedrebbe
      // mai le richieste in arrivo. Le tre porte di login lo fanno; il primo
      // claim lo dimenticava.
      setFirebaseUser(auth().currentUser);
      setSigil(clean);
      setPendingPin(null); // wipe PIN dalla memoria

      navigate('/inbox', { replace: true });
    } catch (e) {
      console.error('claim failed', e);
      const m = String(e?.message || '');
      if (m.includes('INVITE_REQUIRED')) {
        setErr('Nullum è su invito. Ti serve un link valido per entrare.');
      } else {
        setErr(e?.message || 'errore nel claim');
      }
      setStage('');
    } finally {
      setBusy(false);
    }
  }

  // Redirect if essential state is missing.
  // Salta se il claim e' appena riuscito: li' pendingPin viene azzerato di
  // proposito e stiamo gia' navigando verso /inbox.
  useEffect(() => {
    if (claimedRef.current) return;
    if (!mnemonic) {
      navigate('/', { replace: true });
    } else if (!pendingPin) {
      navigate('/pin-setup', { replace: true });
    }
  }, [mnemonic, pendingPin, navigate]);

  let badge = null;
  if (status === 'checking') badge = <span className="text-ink-dim">verificando<Cursor /></span>;
  else if (status === 'available') badge = <span className="text-ink text-glow">✓ disponibile</span>;
  else if (status === 'taken') badge = <span className="text-red-500">già preso</span>;
  else if (status === 'invalid') badge = <span className="text-amber">formato non valido</span>;

  return (
    <Frame statusLeft="SETUP :: SIGIL" statusRight="5G">
      <Header title="// Scegli il tuo nome" crumb="[unico]" />
      <OnboardSteps step={4} />

      <p className="text-[15px] text-ink-dim mb-3">
        È il nome con cui ti scrivono.{' '}
        <span className="text-amber">Scegli bene: non si cambia più.</span>
      </p>

      <FieldLabel>&gt; IL TUO NOME (senza @)</FieldLabel>
      <div className="relative mb-1">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-dim pointer-events-none select-none text-[18px]">@</span>
        <input
          type="text"
          name="sigil"
          autoFocus
          autoComplete="off"
          spellCheck="false"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={busy}
          placeholder="iltuonome"
          className="w-full bg-black border border-ink-dim rounded-lg text-ink text-glow font-mono text-[18px] pl-8 pr-3 py-2 focus:border-ink focus:outline-none"
        />
      </div>
      <p className="text-[13px] text-ink-dim mb-3">
        3-20 char :: a-z 0-9 _ &nbsp;::&nbsp; {badge}
      </p>

      <Box className="text-[16px] mb-3">
        <p className="text-ink-dim mb-1">// regole</p>
        <ul className="text-ink-dim text-[13px] pl-3 space-y-0.5">
          <li>• solo tuo, nessun altro può averlo</li>
          <li>• maiuscole e minuscole uguali</li>
          <li>• niente nomi riservati</li>
          <li>• resta per sempre</li>
        </ul>
      </Box>

      {err && (
        <Box variant="danger" className="text-[16px] mb-2">
          <span className="text-red-500">✕</span> {err}
        </Box>
      )}

      {busy && (
        <Box variant="warn" className="text-[16px] mb-2">
          {stage}<Cursor />
        </Box>
      )}

      <label className="flex items-start gap-3 text-[14px] text-ink-dim cursor-pointer mb-2">
        <input
          type="checkbox"
          checked={acceptedTerms}
          onChange={(e) => setAcceptedTerms(e.target.checked)}
          disabled={busy}
          className="w-4 h-4 mt-1 accent-green-500 shrink-0"
        />
        <span>
          Ho letto e accetto i{' '}
          <a href="/termini" target="_blank" rel="noopener noreferrer" className="text-ink underline">Termini di Servizio</a>
          {' '}e l'<a href="/info" target="_blank" rel="noopener noreferrer" className="text-ink underline">Informativa Privacy</a>.
        </span>
      </label>

      <Footer>
        <Btn
          variant="primary"
          onClick={onClaim}
          disabled={busy || status !== 'available' || !acceptedTerms}
        >
          {busy ? '...' : '✦ Conferma e crea'}
        </Btn>
      </Footer>
    </Frame>
  );
}
